import { useState, type ReactNode } from "react";
import { Check, ChevronsUpDown, Globe, Megaphone, Tag, Workflow, Layers } from "lucide-react";
import { Button, SearchCombobox, cn, type ComboItem } from "@fox1ck-org/ui";

import { useT, type TKey } from "@/lib/i18n";
import { formatInt } from "@/lib/format";
import { useDebounced } from "@/lib/hooks/use-debounced";
import { useListOffers, useOffersByIds } from "@/lib/services/offer-service";
import { useListLanders, useLandersByIds } from "@/lib/services/lander-service";
import { useListFlows, useFlowsByIds } from "@/lib/services/flow-service";
import { useListTrafficSources, useTrafficSourcesByIds } from "@/lib/services/traffic-source-service";
import { useListTrackingDomains, useTrackingDomainsByIds } from "@/lib/services/tracking-domain-service";
import { useCampaignsByIds, useListCampaigns } from "@/lib/services/campaign-service";
import { trafficSourceKindLabel } from "@/lib/source-types";

/** The catalogs a picker can choose from. */
export type CatalogKind = "offer" | "lander" | "flow" | "source" | "domain" | "campaign";

export interface CatalogOption {
  id: string;
  label: string;
  sub?: string;
}

/** How many matches a picker lists at once; the rest is reached by typing. */
const PICKER_PAGE = 50;

interface Found {
  items: CatalogOption[];
  total: number;
  loading: boolean;
  error: unknown;
}

/**
 * One catalog, the two ways a picker reads it: the matches of what was typed
 * (the server searches all of it) and the names of the ids already chosen
 * (asked for by id, wherever they sit in the catalog).
 */
interface Adapter {
  useSearch: (q: string, enabled: boolean) => Found;
  useNames: (ids: string[]) => Map<string, CatalogOption>;
}

function found<T>(q: { data?: { items: T[]; total: number }; isLoading: boolean; error: unknown }, map: (row: T) => CatalogOption): Found {
  return { items: (q.data?.items ?? []).map(map), total: q.data?.total ?? 0, loading: q.isLoading, error: q.error };
}

function names<T>(q: { data?: Map<string, T> }, map: (row: T) => CatalogOption): Map<string, CatalogOption> {
  return new Map([...(q.data ?? new Map<string, T>()).values()].map((row) => { const o = map(row); return [o.id, o] as const; }));
}

const offer = (o: { id: string; name: string; country: string[] }): CatalogOption => ({ id: o.id, label: o.name, sub: o.country.join(", ") || undefined });
const lander = (l: { id: string; name: string; language: string; countryTargets: string[] }): CatalogOption => ({ id: l.id, label: l.name, sub: [l.countryTargets.join(", "), l.language].filter(Boolean).join(" · ") || undefined });
const flow = (f: { id: string; name: string }): CatalogOption => ({ id: f.id, label: f.name });
const source = (s: { id: string; name: string; kind: Parameters<typeof trafficSourceKindLabel>[0] }): CatalogOption => ({ id: s.id, label: s.name, sub: trafficSourceKindLabel(s.kind) });
const domain = (d: { id: string; fqdn: string }): CatalogOption => ({ id: d.id, label: d.fqdn });

const ADAPTERS: Record<CatalogKind, Adapter> = {
  offer: {
    useSearch: (q, enabled) => found(useListOffers({ search: q, limit: PICKER_PAGE }, { enabled }), offer),
    useNames: (ids) => names(useOffersByIds(ids), offer),
  },
  lander: {
    useSearch: (q, enabled) => found(useListLanders({ search: q, limit: PICKER_PAGE }, { enabled }), lander),
    useNames: (ids) => names(useLandersByIds(ids), lander),
  },
  flow: {
    useSearch: (q, enabled) => found(useListFlows({ search: q, limit: PICKER_PAGE }, { enabled }), flow),
    useNames: (ids) => names(useFlowsByIds(ids), flow),
  },
  source: {
    useSearch: (q, enabled) => found(useListTrafficSources({ search: q, limit: PICKER_PAGE }, { enabled }), source),
    useNames: (ids) => names(useTrafficSourcesByIds(ids), source),
  },
  domain: {
    useSearch: (q, enabled) => found(useListTrackingDomains({ search: q, limit: PICKER_PAGE }, { enabled }), domain),
    useNames: (ids) => names(useTrackingDomainsByIds(ids), domain),
  },
  campaign: {
    useSearch: (q, enabled) =>
      found(useListCampaigns({ search: q, limit: PICKER_PAGE }, { enabled }), (i) => ({ id: i.campaign?.id ?? "", label: i.campaign?.name ?? "" })),
    useNames: (ids) => {
      const q = useCampaignsByIds(ids);
      return new Map([...(q.data?.values() ?? [])].map((i) => [i.campaign!.id, { id: i.campaign!.id, label: i.campaign!.name }] as const));
    },
  },
};

const ICON: Record<CatalogKind, typeof Tag> = { offer: Tag, lander: Globe, flow: Workflow, source: Megaphone, domain: Globe, campaign: Layers };

const WORDS: Record<CatalogKind, { pick: TKey; search: TKey; none: TKey }> = {
  offer: { pick: "picker.offer.pick", search: "picker.offer.search", none: "picker.offer.none" },
  lander: { pick: "picker.lander.pick", search: "picker.lander.search", none: "picker.lander.none" },
  flow: { pick: "picker.flow.pick", search: "picker.flow.search", none: "picker.flow.none" },
  source: { pick: "picker.source.pick", search: "picker.source.search", none: "picker.source.none" },
  domain: { pick: "picker.domain.pick", search: "picker.domain.search", none: "picker.domain.none" },
  campaign: { pick: "picker.campaign.pick", search: "picker.campaign.search", none: "picker.campaign.none" },
};

/** The name of one id of a catalog, read by id: «—» while it loads, a short id if it is gone. */
export function useCatalogNames(kind: CatalogKind, ids: string[]): Map<string, CatalogOption> {
  return ADAPTERS[kind].useNames(ids);
}

export interface CatalogPickerProps {
  /** Fixed for the life of the picker (key the element by it if it can change). */
  kind: CatalogKind;
  value?: string;
  onChange: (id: string) => void;
  /** Offer «none» as the first choice; picking it calls onChange(""). */
  clearable?: boolean;
  /** Ids not to offer (already chosen elsewhere). */
  exclude?: string[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Extra line under an option (usage, payout…). */
  renderSub?: (o: CatalogOption) => ReactNode;
  /**
   * A filter chip on a list toolbar («Кампания: все») instead of a form
   * field: `label` is the chip's name, no value reads «все», and the first
   * choice clears it.
   */
  chip?: { label: string };
  "aria-label"?: string;
}

/**
 * Pick one row of a catalog of any size. What is typed is searched by the
 * server over the WHOLE catalog; the list shows the first PICKER_PAGE matches
 * and says how many more there are; the chosen value is named by its id, so a
 * pick from deep in the catalog never shows as a bare id.
 */
export function CatalogPicker({ kind, value, onChange, clearable, exclude, placeholder, disabled, className, renderSub, chip, ...rest }: CatalogPickerProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const q = useDebounced(typed.trim());
  const adapter = ADAPTERS[kind];
  const result = adapter.useSearch(q, open);
  const chosen = adapter.useNames(value ? [value] : []).get(value ?? "");
  const words = WORDS[kind];
  const Icon = ICON[kind];
  const skip = new Set(exclude ?? []);
  const items = result.items.filter((o) => !skip.has(o.id));
  const more = result.total - result.items.length;

  const pick = (id: string) => {
    onChange(id);
    setOpen(false);
    setTyped("");
  };

  const chosenLabel = value ? (chosen?.label ?? `${value.slice(0, 8)}…`) : undefined;
  const allowClear = clearable || !!chip;

  const mark = (on: boolean) => <Check className={cn("size-3.5 shrink-0", on ? "opacity-100" : "opacity-0")} aria-hidden />;
  const rows: ComboItem[] = [
    ...(allowClear ? [{ id: "", title: <span className="inline-flex items-center gap-2 text-muted-foreground">{mark(!value)}{chip ? t("filter.all") : t(words.none)}</span> }] : []),
    ...items.map((o) => ({
      id: o.id,
      title: (
        <span className="inline-flex min-w-0 items-center gap-2">
          {mark(o.id === value)}
          <span className="truncate">{o.label}</span>
        </span>
      ),
      subtitle: renderSub ? renderSub(o) : o.sub,
    })),
  ];
  const groups = [{ id: kind, label: t(words.pick), items: rows }];

  const trigger = chip ? (
    <button
      type="button"
      aria-label={chip.label}
      disabled={disabled}
      className={cn(
        "inline-flex h-7 max-w-72 shrink-0 items-center gap-1.5 rounded-full border bg-card px-3 text-sm",
        value ? "border-foreground" : "border-border-strong",
        className,
      )}
    >
      <span className="text-muted-foreground">{chip.label}</span>
      <b className="truncate font-semibold text-foreground">{chosenLabel ?? t("filter.all")}</b>
    </button>
  ) : (
    <Button
      type="button"
      variant="secondary"
      role="combobox"
      aria-expanded={open}
      aria-label={rest["aria-label"]}
      disabled={disabled}
      className={cn("w-full justify-between", className)}
    >
      <span className="flex min-w-0 items-center gap-2 text-left">
        <Icon className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        {chosenLabel ? <span className="truncate">{chosenLabel}</span> : <span className="truncate text-muted-foreground">{placeholder ?? t(words.pick)}</span>}
      </span>
      <ChevronsUpDown className="ml-2 size-3.5 shrink-0 opacity-50" />
    </Button>
  );

  return (
    <SearchCombobox
      query={typed}
      onQueryChange={setTyped}
      state={result.error ? "error" : result.loading ? "loading" : rows.length === 0 ? "empty" : "results"}
      groups={groups}
      idleGroups={groups}
      minChars={0}
      highlightQuery={false}
      placeholder={t(words.search)}
      label={rest["aria-label"] ?? chip?.label ?? t(words.pick)}
      emptyText={t("picker.nothing")}
      errorText={t("picker.failed")}
      footer={more > 0 ? <span className="text-caption">{t("picker.more", { count: formatInt(more) })}</span> : undefined}
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTyped("");
      }}
      align="start"
      labels={{ navigate: t("picker.keys.move"), choose: t("picker.keys.pick") }}
      onSelect={(item) => pick(item.id)}
      trigger={trigger}
    />
  );
}
